/*
 * gilbi-tryon.js — Prova virtuale del capo con la fotocamera.
 *
 * Due modalita', con passaggio automatico:
 *   auto    -> MediaPipe Pose Landmarker: il capo viene ancorato a spalle o fianchi,
 *              scalato e ruotato in tempo reale seguendo la persona.
 *   manuale -> l'utente sposta il capo con un dito, ridimensiona e ruota con due dita
 *              (rotellina del mouse su desktop).
 *
 * Il ripiego su "manuale" scatta da solo se: il browser non supporta i moduli ES,
 * la CDN non risponde, il modello non si carica o la posa non viene rilevata.
 *
 * Nessuna immagine lascia il dispositivo: tutto avviene nel canvas del browser.
 */
(function () {
  'use strict';

  var VISION_CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14';
  var VISION_MODULE = VISION_CDN + '/vision_bundle.mjs';
  var MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';

  var LM = { L_SHOULDER: 11, R_SHOULDER: 12, L_HIP: 23, R_HIP: 24 };
  var CAMERA_TIMEOUT = 25000;   // il prompt dei permessi puo' restare appeso senza mai rispondere
  var POSE_TIMEOUT = 12000;
  var DETECT_INTERVAL = 50;     // ~20 rilevamenti al secondo: piu' non serve e scalda il telefono
  var DRAG_THRESHOLD = 6;       // px prima di considerare un tocco come trascinamento

  var UA = navigator.userAgent || '';
  var IS_IOS = /iPad|iPhone|iPod/.test(UA) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  var IS_INAPP = /FBAN|FBAV|FB_IAB|Instagram|Line\/|TikTok|Snapchat|Pinterest|MicroMessenger|Twitter/i.test(UA);

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }

  var ICON = {
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    flip: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h3l2-2h6l2 2h3v12H4z"/><path d="M12 9v6M9.5 11.5L12 9l2.5 2.5"/></svg>',
    hand: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11V5.5a1.5 1.5 0 013 0V11m0-1.5a1.5 1.5 0 013 0V12m0-1a1.5 1.5 0 013 0v4a5 5 0 01-5 5h-2a5 5 0 01-5-5v-4.5a1.5 1.5 0 013 0"/></svg>'
  };

  function GilbiTryon(host) {
    this.host = host;
    this.cfg = {
      src: host.getAttribute('data-image'),
      anchor: (host.getAttribute('data-anchor') || 'shoulders').toLowerCase(),
      scale: parseFloat(host.getAttribute('data-scale')) || 2.6,
      offsetY: parseFloat(host.getAttribute('data-offset-y')) || 0,
      title: host.getAttribute('data-title') || '',
      legal: host.getAttribute('data-legal') || '',
      hintAuto: host.getAttribute('data-hint-auto') || 'Inquadrati a mezzo busto, con le spalle ben visibili.',
      hintManual: host.getAttribute('data-hint-manual') || 'Trascina per spostare il capo, due dita per ridimensionarlo e ruotarlo.',
      demo: host.getAttribute('data-demo') === '1'
    };
    this.mode = 'auto';
    this.facing = 'user';
    this.mirror = true;
    this.frozen = false;
    this.smooth = null;
    this.manual = null;
    this.missed = 0;
    this.tainted = false;
    this.photo = null;        // <img> caricata dalla galleria, alternativa al video
    this.shotUrl = null;      // objectURL dell'ultimo scatto
    this.pointers = new Map();
    this.gestureStart = null;
    this.dragging = false;

    var self = this;
    host.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[data-gtryon-open]')) {
        e.preventDefault();
        self.open();
      }
    });
  }

  /* ---------------- Costruzione dell'interfaccia ---------------- */

  GilbiTryon.prototype.build = function () {
    if (this.root) return;
    var self = this;
    var root = el('div', 'gtryon-overlay');
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');

    root.innerHTML =
      '<div class="gtryon-top">' +
        '<p class="gtryon-title"></p>' +
        '<div class="gtryon-actions">' +
          '<button type="button" class="gtryon-icon" data-act="mode" aria-pressed="false" title="Posiziona a mano">' + ICON.hand + '</button>' +
          '<button type="button" class="gtryon-icon" data-act="flip" title="Cambia fotocamera">' + ICON.flip + '</button>' +
          '<button type="button" class="gtryon-icon" data-act="close" title="Chiudi">' + ICON.close + '</button>' +
        '</div>' +
      '</div>' +
      '<div class="gtryon-stage">' +
        '<video class="gtryon-video" playsinline muted autoplay></video>' +
        '<canvas class="gtryon-canvas"></canvas>' +
        '<img class="gtryon-shot" alt="" hidden>' +
        '<p class="gtryon-hint" hidden></p>' +
        '<div class="gtryon-msg" hidden></div>' +
      '</div>' +
      '<p class="gtryon-legal"></p>' +
      '<div class="gtryon-bottom">' +
        '<button type="button" class="gtryon-pill gtryon-pill--ghost" data-act="photo">Usa una foto</button>' +
        '<button type="button" class="gtryon-pill gtryon-pill--ghost" data-act="retake" hidden>Riprendi</button>' +
        '<button type="button" class="gtryon-shutter" data-act="shot" aria-label="Scatta"></button>' +
        '<button type="button" class="gtryon-pill" data-act="save" hidden>Salva foto</button>' +
      '</div>' +
      '<input type="file" class="gtryon-file" accept="image/*" data-gtryon-file>';

    this.root = root;
    this.video = root.querySelector('.gtryon-video');
    this.canvas = root.querySelector('.gtryon-canvas');
    this.ctx = this.canvas.getContext('2d');
    this.hint = root.querySelector('.gtryon-hint');
    this.msg = root.querySelector('.gtryon-msg');
    this.btnShot = root.querySelector('[data-act="shot"]');
    this.btnSave = root.querySelector('[data-act="save"]');
    this.btnRetake = root.querySelector('[data-act="retake"]');
    this.btnMode = root.querySelector('[data-act="mode"]');
    this.btnPhoto = root.querySelector('[data-act="photo"]');
    this.btnFlip = root.querySelector('[data-act="flip"]');
    this.imgShot = root.querySelector('.gtryon-shot');
    this.fileInput = root.querySelector('[data-gtryon-file]');
    this.fileInput.addEventListener('change', function (e) {
      var f = e.target.files && e.target.files[0];
      e.target.value = '';
      if (f) self.usePhoto(f);
    });
    root.querySelector('.gtryon-title').textContent =
      this.cfg.demo ? (this.cfg.title + ' · DEMO') : this.cfg.title;
    root.querySelector('.gtryon-legal').textContent = this.cfg.legal;

    root.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-act]') : null;
      if (!b) return;
      var act = b.getAttribute('data-act');
      if (act === 'close') self.close();
      else if (act === 'flip') self.flip();
      else if (act === 'mode') self.toggleMode();
      else if (act === 'shot') self.shoot();
      else if (act === 'save') self.save();
      else if (act === 'retake') self.retake();
      else if (act === 'photo') self.pickPhoto();
    });

    this.onKey = function (e) { if (e.key === 'Escape' && !root.hidden) self.close(); };
    document.addEventListener('keydown', this.onKey);

    this.onVisibility = function () {
      if (document.hidden) self.stopLoop();
      else if (!root.hidden && !self.frozen && (self.stream || self.photo)) self.loop();
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    this.bindGestures();
    document.body.appendChild(root);
  };

  GilbiTryon.prototype.destroy = function () {
    this.close();
    if (this.onKey) document.removeEventListener('keydown', this.onKey);
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.pose && this.pose.close) { try { this.pose.close(); } catch (e) {} }
    this.pose = null;
    if (this.root && this.root.parentNode) this.root.parentNode.removeChild(this.root);
    this.root = null;
  };

  /* ---------------- Messaggi a tutto schermo ---------------- */

  GilbiTryon.prototype.say = function (title, body, actionLabel, action, spinner) {
    var self = this;
    this.msg.innerHTML = '';
    if (spinner) this.msg.appendChild(el('div', 'gtryon-spinner'));
    if (title) this.msg.appendChild(el('h3', null, title));
    if (body) this.msg.appendChild(el('p', null, body));
    // Ogni messaggio senza spinner deve offrire una via d'uscita, altrimenti si resta bloccati.
    if (!actionLabel && !spinner) { actionLabel = 'Chiudi'; action = function () { self.close(); }; }
    if (actionLabel) {
      var b = el('button', 'gtryon-pill', actionLabel);
      b.type = 'button';
      b.addEventListener('click', action);
      this.msg.appendChild(b);
    }
    this.msg.hidden = false;
  };
  GilbiTryon.prototype.hideMsg = function () { if (this.msg) this.msg.hidden = true; };

  /* ---------------- Apertura e chiusura ---------------- */

  GilbiTryon.prototype.open = function () {
    this.build();
    this.root.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    this.retake();

    if (IS_INAPP && IS_IOS) {
      var self = this;
      this.say(
        'Apri in Safari',
        'Su iPhone la fotocamera non funziona dentro il browser di Instagram, Facebook o TikTok. Tocca i tre puntini in alto e scegli "Apri in Safari".',
        'Copia il link',
        function () {
          var done = function () {
            self.say('Link copiato', 'Incollalo in Safari per provare il capo.');
          };
          try {
            var r = navigator.clipboard && navigator.clipboard.writeText(location.href);
            if (r && r.then) r.then(done, function () {
              self.say('Copia il link a mano', location.href);
            });
            else done();
          } catch (e) {
            self.say('Copia il link a mano', location.href);
          }
        }
      );
      return;
    }
    this.start();
  };

  GilbiTryon.prototype.stopStream = function () {
    if (this.stream) {
      this.stream.getTracks().forEach(function (t) { t.stop(); });
      this.stream = null;
    }
    if (this.video) this.video.srcObject = null;
  };

  GilbiTryon.prototype.close = function () {
    if (!this.root) return;
    this.root.hidden = true;
    document.documentElement.style.overflow = '';
    this.stopLoop();
    this.stopStream();
    clearTimeout(this.hintTimer);
    this.hintFading = false;
    this.pointers.clear();
    this.gestureStart = null;
    this.dragging = false;
    this.retake();          // ripristina otturatore / salva / riprendi / usa una foto
    this.photo = null;
    if (this.btnPhoto) this.btnPhoto.textContent = 'Usa una foto';
    if (this.btnFlip) this.btnFlip.hidden = false;
    this.mode = 'auto';
    if (this.btnMode) this.btnMode.setAttribute('aria-pressed', 'false');
    this.smooth = null; this.manual = null;
    this.hideMsg();
  };

  /* ---------------- Avvio ---------------- */

  GilbiTryon.prototype.start = function () {
    var self = this;
    this.say(null, 'Attivazione fotocamera…', null, null, true);

    this.loadGarment().catch(function () {
      self.say('Immagine non disponibile', 'Il capo per la prova virtuale non è stato caricato correttamente.');
      throw new Error('garment');
    }).then(function () {
      return self.startCamera();
    }).then(function () {
      self.hideMsg();
      self.showHint(self.cfg.hintAuto);
      self.loop();
      return self.loadPose();
    }).then(function (ok) {
      if (!ok) self.setMode('manual');
    }).catch(function (err) {
      if (err && err.message === 'garment') return;
      self.cameraError(err);
    });
  };

  GilbiTryon.prototype.loadGarment = function () {
    var self = this;
    if (this.garment) return Promise.resolve(this.garment);
    // Primo tentativo con CORS, cosi' il canvas resta "pulito" e la foto e' salvabile.
    // Se la CDN non manda gli header CORS ripieghiamo senza: si perde il salvataggio,
    // ma l'anteprima continua a funzionare (e lo diciamo all'utente prima dello scatto).
    function load(useCors) {
      return new Promise(function (res, rej) {
        var img = new Image();
        if (useCors) img.crossOrigin = 'anonymous';
        img.onload = function () { res(img); };
        img.onerror = rej;
        img.src = self.cfg.src;
      });
    }
    return load(true).catch(function () {
      self.tainted = true;
      return load(false);
    }).then(function (img) { self.garment = img; return img; });
  };

  GilbiTryon.prototype.startCamera = function () {
    var self = this;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return Promise.reject({ name: 'Unsupported' });
    }
    this.stopStream();

    var ask = navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: self.facing, width: { ideal: 1280 }, height: { ideal: 960 } }
    });
    var timeout = new Promise(function (_, rej) {
      setTimeout(function () { rej({ name: 'TimeoutError' }); }, CAMERA_TIMEOUT);
    });

    return Promise.race([ask, timeout]).then(function (stream) {
      self.stream = stream;
      self.video.srcObject = stream;
      // Se la fotocamera viene spenta da fuori (chiamata in arrivo, altra app) lo diciamo.
      stream.getTracks().forEach(function (t) {
        t.addEventListener('ended', function () {
          if (self.root && !self.root.hidden) {
            self.stopLoop();
            self.say('Fotocamera interrotta', 'La fotocamera è stata chiusa da un\'altra applicazione.',
              'Riprova', function () { self.start(); });
          }
        });
      });
      return self.video.play().catch(function () {});
    }).then(function () {
      return new Promise(function (res) {
        if (self.video.videoWidth) return res();
        self.video.addEventListener('loadedmetadata', function () { res(); }, { once: true });
        setTimeout(res, 4000);
      });
    }).then(function () {
      if (!self.video.videoWidth) throw { name: 'NotReadableError' };
      self.syncCanvas();
      self.manual = null;
      self.smooth = null;
      self.mirror = (self.facing === 'user');
    });
  };

  GilbiTryon.prototype.syncCanvas = function () {
    // Su iOS le dimensioni del flusso si scambiano quando il telefono ruota:
    // vanno ricontrollate a ogni frame, non solo all'avvio.
    var w = this.video.videoWidth, h = this.video.videoHeight;
    if (!w || !h) return false;
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.manual = null;
      this.smooth = null;
    }
    return true;
  };

  GilbiTryon.prototype.cameraError = function (err) {
    var name = (err && err.name) || '';
    var self = this;
    var retry = function () { self.start(); };
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      this.say('Permesso negato',
        'Per provare il capo devi consentire l\'accesso alla fotocamera. Puoi riattivarlo dalle impostazioni del sito nel tuo browser.',
        'Riprova', retry);
    } else if (name === 'TimeoutError') {
      this.say('Nessuna risposta',
        'Non abbiamo ricevuto risposta alla richiesta di accesso alla fotocamera.',
        'Riprova', retry);
    } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      this.say('Nessuna fotocamera',
        'Non abbiamo trovato una fotocamera utilizzabile su questo dispositivo.',
        'Chiudi', function () { self.close(); });
    } else if (name === 'NotReadableError') {
      this.say('Fotocamera occupata',
        'Un\'altra applicazione sta già usando la fotocamera. Chiudila e riprova.',
        'Riprova', retry);
    } else {
      this.say('Fotocamera non disponibile',
        'Il tuo browser non permette l\'accesso alla fotocamera su questa pagina. Prova ad aprire il sito in Safari o Chrome.',
        'Chiudi', function () { self.close(); });
    }
  };

  /* ---------------- MediaPipe (modalita' automatica) ---------------- */

  GilbiTryon.prototype.loadPose = function () {
    var self = this;
    if (this.pose) return Promise.resolve(true);
    if (typeof window.WebAssembly === 'undefined') return Promise.resolve(false);

    // import() dietro new Function: i browser che non lo supportano fallirebbero il
    // parsing dell'intero file, portandosi via anche il ripiego manuale.
    var dynImport;
    try { dynImport = new Function('u', 'return import(u);'); } catch (e) { return Promise.resolve(false); }

    var timeout = new Promise(function (res) { setTimeout(function () { res('timeout'); }, POSE_TIMEOUT); });
    var load = dynImport(VISION_MODULE).then(function (vision) {
      return vision.FilesetResolver.forVisionTasks(VISION_CDN + '/wasm').then(function (fileset) {
        function create(delegate) {
          return vision.PoseLandmarker.createFromOptions(fileset, {
            baseOptions: { modelAssetPath: MODEL_URL, delegate: delegate },
            runningMode: 'VIDEO',
            numPoses: 1,
            minPoseDetectionConfidence: 0.5,
            minTrackingConfidence: 0.5
          });
        }
        return create('GPU').catch(function () { return create('CPU'); });
      });
    }).then(function (pose) { self.pose = pose; return true; });

    return Promise.race([load, timeout])
      .then(function (r) { return r !== 'timeout'; })
      .catch(function () { return false; });
  };

  /* ---------------- Loop di rendering ---------------- */

  GilbiTryon.prototype.loop = function () {
    var self = this;
    this.stopLoop();
    var tick = function () {
      self.raf = requestAnimationFrame(tick);
      if (self.frozen) return;
      self.draw();
    };
    this.raf = requestAnimationFrame(tick);
  };
  GilbiTryon.prototype.stopLoop = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  };

  GilbiTryon.prototype.draw = function () {
    var source;
    if (this.photo) {
      source = this.photo;                       // foto dalla galleria: canvas gia' dimensionato
    } else {
      if (this.video.readyState < 2 || !this.syncCanvas()) return;
      source = this.video;
    }
    var c = this.ctx, W = this.canvas.width, H = this.canvas.height;

    // Specchiamo solo la fotocamera frontale: sulla posteriore, e su una foto
    // caricata, l'immagine deve restare com'e' o scritte e scena si rovesciano.
    c.save();
    if (this.mirror) c.setTransform(-1, 0, 0, 1, W, 0);
    c.drawImage(source, 0, 0, W, H);
    c.restore();

    var box = this.mode === 'auto' ? this.autoBox(W, H) : this.currentManual(W, H);
    if (!box) return;

    var g = this.garment;
    var w = box.w;
    var h = w * (g.naturalHeight / g.naturalWidth);
    c.save();
    c.translate(box.cx, box.cy + h * this.cfg.offsetY);
    c.rotate(box.angle);
    c.drawImage(g, -w / 2, 0, w, h);
    c.restore();
  };

  GilbiTryon.prototype.autoBox = function (W, H) {
    if (!this.pose) return this.defaultBox(W, H);

    // Su una foto ferma la posa e' gia' stata calcolata una volta in detectOnPhoto:
    // qui non va rifatto nulla (detectForVideo lavora sul <video>, che non c'e').
    if (this.photo) return this.smooth ? this.fromSmooth() : this.defaultBox(W, H);

    var now = performance.now();
    if (now - (this.lastDetect || 0) < DETECT_INTERVAL) {
      return this.smooth ? this.fromSmooth() : null;
    }
    this.lastDetect = now;

    var res;
    try { res = this.pose.detectForVideo(this.video, now); } catch (e) { return this.defaultBox(W, H); }
    var lms = res && res.landmarks && res.landmarks[0];

    if (!lms) {
      this.missed++;
      if (this.missed === 40) this.showHint(this.cfg.hintAuto);
      return this.smooth ? this.fromSmooth() : null;
    }
    this.missed = 0;

    var target = this.boxFromLandmarks(lms, W, H);
    if (!target) return this.smooth ? this.fromSmooth() : null;
    this.hideHint();

    if (!this.smooth) this.smooth = target;
    else {
      var t = 0.35;
      this.smooth = {
        cx: lerp(this.smooth.cx, target.cx, t),
        cy: lerp(this.smooth.cy, target.cy, t),
        w: lerp(this.smooth.w, target.w, t),
        angle: lerp(this.smooth.angle, target.angle, t)
      };
    }
    return this.fromSmooth();
  };

  // Da spalle (o fianchi) a riquadro sul canvas. Usata sia dal video sia dalla foto.
  GilbiTryon.prototype.boxFromLandmarks = function (lms, W, H) {
    var useHips = this.cfg.anchor === 'hips';
    var a = lms[useHips ? LM.L_HIP : LM.L_SHOULDER];
    var b = lms[useHips ? LM.R_HIP : LM.R_SHOULDER];
    if (!a || !b) return null;

    // Alcune build di MediaPipe riempiono "visibility" con 0 su tutti i punti:
    // scartare a 0 spegnerebbe il tracking per sempre. Filtriamo solo valori reali.
    var va = a.visibility, vb = b.visibility;
    if ((va > 0 || vb > 0) && (va < 0.4 || vb < 0.4)) return null;

    var ax = this.mirror ? (1 - a.x) * W : a.x * W, ay = a.y * H;
    var bx = this.mirror ? (1 - b.x) * W : b.x * W, by = b.y * H;
    var dist = Math.hypot(bx - ax, by - ay);
    if (!dist) return null;

    var box = {
      cx: (ax + bx) / 2,
      cy: (ay + by) / 2,
      w: dist * this.cfg.scale,
      angle: Math.atan2(by - ay, bx - ax)
    };
    if (box.angle > Math.PI / 2) box.angle -= Math.PI;
    if (box.angle < -Math.PI / 2) box.angle += Math.PI;
    return box;
  };

  GilbiTryon.prototype.fromSmooth = function () {
    var s = this.smooth;
    return { cx: s.cx, cy: s.cy, w: s.w, angle: s.angle };
  };

  // Posizione di partenza. NON scrive su this.manual: se lo facesse, il primo
  // frame disegnato prima che il modello sia pronto congelerebbe il capo al centro
  // e il passaggio a "manuale" perderebbe la posizione gia' agganciata al corpo.
  GilbiTryon.prototype.defaultBox = function (W, H) {
    return { cx: W / 2, cy: H * 0.28, w: W * 0.55, angle: 0 };
  };

  GilbiTryon.prototype.currentManual = function (W, H) {
    if (!this.manual) this.manual = this.smooth ? this.fromSmooth() : this.defaultBox(W, H);
    return this.manual;
  };

  /* ---------------- Gesti (modalita' manuale) ---------------- */

  GilbiTryon.prototype.bindGestures = function () {
    var self = this, cv = this.canvas;

    function toCanvas(e) {
      var r = cv.getBoundingClientRect();
      if (!r.width || !r.height) return { x: 0, y: 0 };
      return { x: (e.clientX - r.left) * (cv.width / r.width), y: (e.clientY - r.top) * (cv.height / r.height) };
    }
    function twoFingers() {
      var p = Array.from(self.pointers.values());
      return { dist: Math.hypot(p[1].x - p[0].x, p[1].y - p[0].y), ang: Math.atan2(p[1].y - p[0].y, p[1].x - p[0].x) };
    }
    // Va ricalcolata ogni volta che si TORNA a due dita, non solo la prima:
    // con un terzo dito appoggiato e poi sollevato la base sarebbe sfasata.
    function rebase() {
      if (self.pointers.size !== 2) { self.gestureStart = null; return; }
      self.currentManual(cv.width, cv.height);
      self.gestureStart = { m: Object.assign({}, self.manual), g: twoFingers() };
    }

    cv.addEventListener('pointerdown', function (e) {
      if (self.frozen) return;
      try { cv.setPointerCapture(e.pointerId); } catch (err) {}
      self.pointers.set(e.pointerId, toCanvas(e));
      self.dragOrigin = toCanvas(e);
      self.dragging = false;
      rebase();
    });

    cv.addEventListener('pointermove', function (e) {
      if (!self.pointers.has(e.pointerId) || self.frozen) return;
      var prev = self.pointers.get(e.pointerId);
      var cur = toCanvas(e);
      self.pointers.set(e.pointerId, cur);

      // Un semplice tocco non deve far uscire dal tracking automatico:
      // si passa a manuale solo dopo un movimento vero.
      if (!self.dragging) {
        var o = self.dragOrigin || cur;
        if (self.pointers.size < 2 && Math.hypot(cur.x - o.x, cur.y - o.y) < DRAG_THRESHOLD) return;
        self.dragging = true;
        self.setMode('manual');
        if (self.pointers.size === 2) rebase();
      }
      self.currentManual(cv.width, cv.height);

      if (self.pointers.size === 1) {
        self.manual.cx += cur.x - prev.x;
        self.manual.cy += cur.y - prev.y;
      } else if (self.pointers.size === 2 && self.gestureStart) {
        var g = twoFingers(), s0 = self.gestureStart;
        if (s0.g.dist > 0) {
          self.manual.w = clamp(s0.m.w * (g.dist / s0.g.dist), cv.width * 0.1, cv.width * 3);
          self.manual.angle = s0.m.angle + (g.ang - s0.g.ang);
        }
      }
    });

    function release(e) {
      self.pointers.delete(e.pointerId);
      self.dragging = false;
      rebase();
    }
    cv.addEventListener('pointerup', release);
    cv.addEventListener('pointercancel', release);

    cv.addEventListener('wheel', function (e) {
      if (self.frozen) return;
      e.preventDefault();
      self.setMode('manual');
      self.currentManual(cv.width, cv.height);
      self.manual.w = clamp(self.manual.w * (e.deltaY < 0 ? 1.06 : 0.94), cv.width * 0.1, cv.width * 3);
    }, { passive: false });
  };

  GilbiTryon.prototype.setMode = function (mode) {
    if (this.mode === mode) return;
    if (mode === 'manual') {
      // Parte dalla posizione gia' agganciata al corpo, non dal centro.
      this.manual = this.smooth ? this.fromSmooth() : this.defaultBox(this.canvas.width, this.canvas.height);
    }
    this.mode = mode;
    this.btnMode.setAttribute('aria-pressed', String(mode === 'manual'));
    this.showHint(mode === 'manual' ? this.cfg.hintManual : this.cfg.hintAuto);
  };
  GilbiTryon.prototype.toggleMode = function () {
    if (this.mode === 'manual') {
      if (this.pose) this.setMode('auto');
      else this.showHint('Tracciamento non disponibile: posiziona il capo a mano.');
    } else {
      this.setMode('manual');
    }
  };

  GilbiTryon.prototype.showHint = function (text) {
    var self = this;
    clearTimeout(this.hintTimer);
    this.hintFading = false;
    this.hint.textContent = text;
    this.hint.hidden = false;
    this.hint.style.opacity = '1';
    this.hintTimer = setTimeout(function () { self.hideHint(); }, 5000);
  };
  GilbiTryon.prototype.hideHint = function () {
    var self = this;
    // Senza questa guardia autoBox rilancerebbe il timer 20 volte al secondo
    // e il suggerimento non sparirebbe mai davvero dal DOM.
    if (this.hint.hidden || this.hintFading) return;
    this.hintFading = true;
    this.hint.style.opacity = '0';
    clearTimeout(this.hintTimer);
    this.hintTimer = setTimeout(function () {
      self.hint.hidden = true;
      self.hintFading = false;
    }, 400);
  };

  GilbiTryon.prototype.pickPhoto = function () {
    if (this.photo) { this.backToCamera(); return; }
    this.fileInput.click();
  };

  GilbiTryon.prototype.usePhoto = function (file) {
    var self = this;
    this.say(null, 'Caricamento foto…', null, null, true);
    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      self.stopLoop();
      self.stopStream();                  // con una foto la fotocamera non serve piu'
      self.photo = img;
      self.mirror = false;                // una foto non va mai specchiata
      var W = img.naturalWidth, H = img.naturalHeight;
      var MAX = 1600;                     // tetto ai megapixel: canvas enormi bloccano i telefoni
      if (Math.max(W, H) > MAX) { var k = MAX / Math.max(W, H); W = Math.round(W * k); H = Math.round(H * k); }
      self.canvas.width = W; self.canvas.height = H;
      self.manual = null; self.smooth = null;
      self.btnPhoto.textContent = 'Torna alla fotocamera';
      self.btnFlip.hidden = true;
      self.retake();
      self.hideMsg();
      self.loop();
      self.detectOnPhoto();
      URL.revokeObjectURL(url);
    };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      self.say('Foto non leggibile', 'Questo file non è un\'immagine che il browser sa aprire.',
        'Riprova', function () { self.hideMsg(); self.pickPhoto(); });
    };
    img.src = url;
  };

  // Su una foto ferma il rilevamento va fatto in modalita' IMAGE, una volta sola.
  GilbiTryon.prototype.detectOnPhoto = function () {
    var self = this;
    if (!this.pose || !this.photo) { this.setMode('manual'); return; }
    try {
      var r = this.pose.setOptions({ runningMode: 'IMAGE' });
      Promise.resolve(r).then(function () {
        var res = self.pose.detect(self.photo);
        var lms = res && res.landmarks && res.landmarks[0];
        if (!lms) { self.setMode('manual'); self.showHint('Corpo non riconosciuto: posiziona il capo a mano.'); return; }
        self.smooth = self.boxFromLandmarks(lms, self.canvas.width, self.canvas.height);
        if (!self.smooth) { self.setMode('manual'); return; }
        self.mode = 'auto';
        self.btnMode.setAttribute('aria-pressed', 'false');
        self.showHint('Puoi correggere la posizione con le dita.');
      }).catch(function () { self.setMode('manual'); });
    } catch (e) { this.setMode('manual'); }
  };

  GilbiTryon.prototype.backToCamera = function () {
    var self = this;
    this.photo = null;
    this.smooth = null; this.manual = null;
    this.btnPhoto.textContent = 'Usa una foto';
    this.btnFlip.hidden = false;
    this.retake();
    if (this.pose) { try { Promise.resolve(this.pose.setOptions({ runningMode: 'VIDEO' })).catch(function () {}); } catch (e) {} }
    this.mode = 'auto';
    this.btnMode.setAttribute('aria-pressed', 'false');
    this.say(null, 'Attivazione fotocamera…', null, null, true);
    this.startCamera().then(function () { self.hideMsg(); self.loop(); })
                      .catch(function (e) { self.cameraError(e); });
  };

  GilbiTryon.prototype.flip = function () {
    var self = this;
    var previous = this.facing;
    this.facing = previous === 'user' ? 'environment' : 'user';
    this.stopLoop();
    this.startCamera().then(function () {
      self.hideMsg();
      self.loop();
    }).catch(function () {
      // Se la seconda fotocamera non si apre, torniamo a quella che funzionava.
      self.facing = previous;
      self.startCamera().then(function () {
        self.hideMsg();
        self.loop();
        self.showHint('Questo dispositivo ha una sola fotocamera utilizzabile.');
      }).catch(function (e2) { self.cameraError(e2); });
    });
  };

  /* ---------------- Scatto e salvataggio ---------------- */

  GilbiTryon.prototype.shoot = function () {
    var self = this;
    this.frozen = true;
    this.btnShot.hidden = true;
    this.btnSave.hidden = false;
    this.btnRetake.hidden = false;
    this.btnPhoto.hidden = true;
    this.hideHint();

    if (this.tainted) {
      this.showHint('Salvataggio non disponibile: fai uno screenshot.');
      return;
    }
    // Lo scatto diventa una <img> vera sopra al canvas: su iPhone basta tenerla
    // premuta e scegliere "Aggiungi a Foto", che e' il salvataggio piu' affidabile.
    try {
      this.canvas.toBlob(function (blob) {
        if (!blob || !self.imgShot) return;
        self.releaseShot();
        self.shotUrl = URL.createObjectURL(blob);
        self.shotBlob = blob;
        self.imgShot.src = self.shotUrl;
        self.imgShot.hidden = false;
        self.showHint(IS_IOS ? 'Tieni premuto sulla foto per salvarla, oppure usa Salva foto.'
                             : 'Usa Salva foto per scaricarla.');
      }, 'image/jpeg', 0.92);
    } catch (e) {
      this.tainted = true;
      this.showHint('Salvataggio non disponibile: fai uno screenshot.');
    }
  };

  GilbiTryon.prototype.releaseShot = function () {
    if (this.shotUrl) { URL.revokeObjectURL(this.shotUrl); this.shotUrl = null; }
    this.shotBlob = null;
  };

  GilbiTryon.prototype.retake = function () {
    if (!this.root) return;
    this.frozen = false;
    this.btnShot.hidden = false;
    this.btnSave.hidden = true;
    this.btnRetake.hidden = true;
    this.btnPhoto.hidden = false;
    if (this.imgShot) { this.imgShot.hidden = true; this.imgShot.removeAttribute('src'); }
    this.releaseShot();
  };

  GilbiTryon.prototype.save = function () {
    var self = this;
    var name = (this.cfg.title || 'prova').replace(/[^\w-]+/g, '-').toLowerCase() + '.jpg';

    function download(blob, filename) {
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(function () { URL.revokeObjectURL(url); }, 8000);
    }

    function handle(blob) {
      if (!blob) return;
      var file = null;
      try { file = new File([blob], name, { type: 'image/jpeg' }); } catch (e) {}
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        navigator.share({ files: [file] }).catch(function (err) {
          // AbortError = l'utente ha chiuso il pannello di condivisione: non e' un errore,
          // e non deve tradursi in un download che nessuno ha chiesto.
          if (err && err.name === 'AbortError') return;
          download(blob, name);
        });
      } else {
        download(blob, name);
      }
    }

    if (this.shotBlob) { handle(this.shotBlob); return; }
    try {
      this.canvas.toBlob(handle, 'image/jpeg', 0.92);
    } catch (e) {
      this.say('Impossibile salvare',
        'Il browser ha bloccato il salvataggio dell\'immagine. Puoi comunque fare uno screenshot.',
        'Torna alla foto', function () { self.hideMsg(); });
    }
  };

  /* ---------------- Avvio ---------------- */

  var instances = new WeakMap();

  function init(scope) {
    var nodes = (scope || document).querySelectorAll('[data-gtryon]:not([data-gtryon-ready])');
    Array.prototype.forEach.call(nodes, function (host) {
      host.setAttribute('data-gtryon-ready', '');
      instances.set(host, new GilbiTryon(host));
    });
  }
  function teardown(scope) {
    var nodes = (scope || document).querySelectorAll('[data-gtryon][data-gtryon-ready]');
    Array.prototype.forEach.call(nodes, function (host) {
      var inst = instances.get(host);
      if (inst) { inst.destroy(); instances.delete(host); }
      host.removeAttribute('data-gtryon-ready');
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  // Nel Personalizza le sezioni vengono ricaricate di continuo: senza teardown
  // resterebbero in memoria un overlay e un modello WASM per ogni ricarica.
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  window.GilbiTryonInit = init;
})();
